import { motion } from "framer-motion";
import {
  ResponsiveContainer, RadarChart, PolarGrid, PolarAngleAxis, Radar,
  AreaChart, Area, XAxis, YAxis, Tooltip, BarChart, Bar, Cell,
} from "recharts";

const gridStroke = "rgba(255,255,255,0.06)";
const axisTick = { fill: "rgba(255,255,255,0.4)", fontSize: 10, fontFamily: "JetBrains Mono" };

function TT({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="nx-panel px-3 py-2 text-xs">
      <div className="font-mono text-[10px] text-white/40">{label}</div>
      {payload.map((p, i) => (
        <div key={i} className="flex items-center gap-2" style={{ color: p.color || p.stroke }}>
          <span>{p.name}</span><span className="font-semibold">{p.value}</span>
        </div>
      ))}
    </div>
  );
}

/* ---------- Neural area (portfolio health timeline) ---------- */
export function NeuralArea({ data, height = 190 }) {
  const pts = data?.length ? data : [];
  const W = 640, H = height, pad = 10;
  if (pts.length < 2) {
    return <div className="flex items-center justify-center text-white/30" style={{ height }}>
      <span className="font-mono text-[11px] tracking-widest nx-blink">STREAMING TELEMETRY…</span>
    </div>;
  }
  const max = Math.max(...pts.map((d) => Math.max(d.health, d.forecast)), 100);
  const min = Math.min(...pts.map((d) => Math.min(d.health, d.forecast)), 40);
  const sx = (i) => pad + (i / (pts.length - 1)) * (W - pad * 2);
  const sy = (v) => H - pad - ((v - min) / (max - min)) * (H - pad * 2);
  const line = (key) => pts.map((d, i) => `${i === 0 ? "M" : "L"}${sx(i)},${sy(d[key])}`).join(" ");
  const area = `${line("health")} L${sx(pts.length - 1)},${H - pad} L${sx(0)},${H - pad} Z`;
  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="w-full" style={{ height }}>
      <defs>
        <linearGradient id="na-fill" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#22d3ee" stopOpacity="0.35" />
          <stop offset="1" stopColor="#22d3ee" stopOpacity="0" />
        </linearGradient>
      </defs>
      {[0.25, 0.5, 0.75].map((g) => (
        <line key={g} x1={pad} x2={W - pad} y1={H * g} y2={H * g} stroke={gridStroke} />
      ))}
      <motion.path d={area} fill="url(#na-fill)" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 1 }} />
      <motion.path d={line("forecast")} fill="none" stroke="#6366f1" strokeWidth="1.5" strokeDasharray="4 5"
        initial={{ pathLength: 0 }} animate={{ pathLength: 1 }} transition={{ duration: 1.6 }} style={{ filter: "drop-shadow(0 0 4px #6366f1)" }} />
      <motion.path d={line("health")} fill="none" stroke="#22d3ee" strokeWidth="2.5"
        initial={{ pathLength: 0 }} animate={{ pathLength: 1 }} transition={{ duration: 1.8 }} style={{ filter: "drop-shadow(0 0 6px #22d3ee)" }} />
      {pts.map((d, i) => i % 4 === 0 && (
        <circle key={i} cx={sx(i)} cy={sy(d.health)} r="2.5" fill="#67e8f9" />
      ))}
    </svg>
  );
}

/* ---------- Risk donut ---------- */
export function RiskDonut({ segments, total, size = 210 }) {
  const stroke = 22, r = (size - stroke) / 2, c = 2 * Math.PI * r;
  let acc = 0;
  return (
    <div className="relative inline-flex items-center justify-center" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="-rotate-90">
        <circle cx={size / 2} cy={size / 2} r={r} stroke="rgba(255,255,255,0.05)" strokeWidth={stroke} fill="none" />
        {segments.map((s, i) => {
          const frac = s.value / total;
          const dash = frac * c;
          const el = (
            <motion.circle key={i} cx={size / 2} cy={size / 2} r={r} stroke={s.color} strokeWidth={stroke} fill="none"
              strokeDasharray={`${dash} ${c - dash}`} strokeDashoffset={-acc}
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: i * 0.15 }}
              style={{ filter: `drop-shadow(0 0 6px ${s.color}77)` }} />
          );
          acc += dash;
          return el;
        })}
      </svg>
      <div className="absolute flex flex-col items-center">
        <span className="font-display text-4xl font-bold text-white">{total}</span>
        <span className="font-mono text-[10px] uppercase tracking-widest text-white/45">Total Risks</span>
      </div>
    </div>
  );
}

/* ---------- Risk heat map ---------- */
const heatColor = (v) =>
  v > 0.8 ? "#ff3b6b" : v > 0.6 ? "#ff9f43" : v > 0.4 ? "#22d3ee" : v > 0.2 ? "#2b6ea3" : "#12324a";

export function HeatMap({ rows = 5, cols = 8, matrix = null, colLabels = null }) {
  if (matrix) {
    return (
      <div className="space-y-1.5" data-testid="risk-heatmap-live">
        {colLabels && (
          <div className="grid gap-1.5 font-mono text-[9px] uppercase tracking-wider text-white/35"
            style={{ gridTemplateColumns: `96px repeat(${colLabels.length}, 1fr)` }}>
            <span />
            {colLabels.map((c) => <span key={c} className="text-center">{c}</span>)}
          </div>
        )}
        {matrix.map((row) => (
          <div key={row.label} className="grid items-center gap-1.5"
            style={{ gridTemplateColumns: `96px repeat(${row.cells.length}, 1fr)` }}>
            <span className="truncate font-mono text-[9px] text-white/45" title={row.label}>{row.label}</span>
            {row.cells.map((c, i) => (
              <motion.div key={i} title={c.title}
                initial={{ opacity: 0, scale: 0.6 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: i * 0.03 }}
                className="flex h-9 items-center justify-center rounded-[4px] font-mono text-[10px] font-bold"
                style={{ background: heatColor(c.v), boxShadow: c.v > 0.6 ? `0 0 10px ${heatColor(c.v)}88` : "none",
                  color: c.v > 0.4 ? "#0b0f1a" : "rgba(255,255,255,0.5)" }}>
                {c.count ?? ""}
              </motion.div>
            ))}
          </div>
        ))}
      </div>
    );
  }
  return <div className="py-6 text-center text-sm text-white/50">No live risk data to chart.</div>;
}

/* ---------- Capability radar ---------- */
export function CapabilityRadar({ data, height = 240 }) {
  return (
    <ResponsiveContainer width="100%" height={height}>
      <RadarChart data={data} outerRadius="72%">
        <PolarGrid stroke={gridStroke} />
        <PolarAngleAxis dataKey="axis" tick={axisTick} />
        <Radar dataKey="value" stroke="#22d3ee" fill="#22d3ee" fillOpacity={0.28} strokeWidth={2} />
      </RadarChart>
    </ResponsiveContainer>
  );
}

/* ---------- Velocity bars ---------- */
export function VelocityBars({ data, height = 200 }) {
  return (
    <ResponsiveContainer width="100%" height={height}>
      <BarChart data={data}>
        <XAxis dataKey="s" tick={axisTick} axisLine={false} tickLine={false} />
        <YAxis hide />
        <Tooltip content={<TT />} cursor={{ fill: "rgba(255,255,255,0.03)" }} />
        <Bar dataKey="v" radius={[4, 4, 0, 0]}>
          {data.map((d, i) => (
            <Cell key={i} fill={i === data.length - 1 ? "#22d3ee" : "#3b5bdb"} />
          ))}
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
}

/* ---------- Forecast confidence band ---------- */
export function ForecastBand({ data, height = 240 }) {
  return (
    <ResponsiveContainer width="100%" height={height}>
      <AreaChart data={data}>
        <defs>
          <linearGradient id="fb-band" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0" stopColor="#6366f1" stopOpacity="0.25" />
            <stop offset="1" stopColor="#6366f1" stopOpacity="0.02" />
          </linearGradient>
        </defs>
        <XAxis dataKey="m" tick={axisTick} axisLine={false} tickLine={false} />
        <YAxis hide domain={[40, 100]} />
        <Tooltip content={<TT />} />
        <Area dataKey="high" stroke="none" fill="url(#fb-band)" />
        <Area dataKey="low" stroke="none" fill="#05070f" />
        <Area dataKey="forecast" stroke="#6366f1" strokeWidth={2} strokeDasharray="4 4" fill="none" />
        <Area dataKey="actual" stroke="#22d3ee" strokeWidth={2.5} fill="none" />
      </AreaChart>
    </ResponsiveContainer>
  );
}

/* ---------- Investment donut ---------- */
export function InvestmentDonut({ data, size = 170 }) {
  const total = data.reduce((a, b) => a + b.value, 0);
  const stroke = 20, r = (size - stroke) / 2, c = 2 * Math.PI * r;
  let acc = 0;
  return (
    <div className="flex items-center gap-6">
      <div className="relative" style={{ width: size, height: size }}>
        <svg width={size} height={size} className="-rotate-90">
          {data.map((s, i) => {
            const dash = (s.value / total) * c;
            const el = (
              <circle key={i} cx={size / 2} cy={size / 2} r={r} stroke={s.color} strokeWidth={stroke} fill="none"
                strokeDasharray={`${dash} ${c - dash}`} strokeDashoffset={-acc}
                style={{ filter: `drop-shadow(0 0 4px ${s.color}66)` }} />
            );
            acc += dash;
            return el;
          })}
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <span className="font-display text-2xl font-bold text-white">$128M</span>
          <span className="font-mono text-[9px] tracking-widest text-white/40">DEPLOYED</span>
        </div>
      </div>
      <div className="space-y-2">
        {data.map((s) => (
          <div key={s.label} className="flex items-center gap-2 text-xs">
            <span className="h-2.5 w-2.5 rounded-sm" style={{ background: s.color }} />
            <span className="text-white/70">{s.label}</span>
            <span className="ml-auto font-mono text-white/45">{s.value}%</span>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ---------- Digital twin dependency network ---------- */
export function DependencyNetwork({ nodes, edges, height = 460 }) {
  const stColor = { ok: "#34d399", watch: "#f59e0b", risk: "#ff3b6b" };
  const pos = (id) => nodes.find((n) => n.id === id);
  return (
    <svg viewBox="0 0 100 100" preserveAspectRatio="xMidYMid meet" className="w-full" style={{ height }}>
      <defs>
        <radialGradient id="tw-core" cx="50%" cy="50%">
          <stop offset="0" stopColor="#22d3ee" stopOpacity="0.9" />
          <stop offset="1" stopColor="#22d3ee" stopOpacity="0" />
        </radialGradient>
      </defs>
      {edges.map(([a, b], i) => {
        const p = pos(a), q = pos(b);
        return (
          <g key={i}>
            <line x1={p.x} y1={p.y} x2={q.x} y2={q.y} stroke="rgba(120,160,255,0.18)" strokeWidth="0.3" />
            <line x1={p.x} y1={p.y} x2={q.x} y2={q.y} stroke="#22d3ee" strokeWidth="0.35" className="nx-flow" opacity="0.6" />
          </g>
        );
      })}
      {nodes.map((n) => (
        <g key={n.id} className="cursor-pointer">
          {!n.hollow && <circle cx={n.x} cy={n.y} r="5.5" fill="url(#tw-core)" className="nx-pulse" />}
          {n.hollow ? (
            <circle cx={n.x} cy={n.y} r="2.4" fill="#05070f" stroke={n.color} strokeWidth="0.45" strokeDasharray="0.9 0.6"
              data-testid={`twin-node-${n.id}`} data-live="false" />
          ) : (
            <circle cx={n.x} cy={n.y} r="2.4" fill={n.color || stColor[n.status]} stroke="#05070f" strokeWidth="0.5"
              data-testid={`twin-node-${n.id}`} data-live="true"
              style={{ filter: `drop-shadow(0 0 3px ${n.color || stColor[n.status]})` }} />
          )}
          <text x={n.x} y={n.y + 8.5} textAnchor="middle" fill="rgba(255,255,255,0.7)"
            style={{ fontSize: 2.6, fontFamily: "JetBrains Mono" }}>{n.label}</text>
        </g>
      ))}
    </svg>
  );
}

/* ---------- Mini feed line ---------- */
export function FeedLine({ data, height = 120, color = "#22d3ee" }) {
  const W = 320, H = height, pad = 6;
  const max = Math.max(...data.map((d) => d.y));
  const min = Math.min(...data.map((d) => d.y));
  const sx = (i) => pad + (i / (data.length - 1)) * (W - pad * 2);
  const sy = (v) => H - pad - ((v - min) / (max - min || 1)) * (H - pad * 2);
  const line = data.map((d, i) => `${i === 0 ? "M" : "L"}${sx(i)},${sy(d.y)}`).join(" ");
  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="w-full" style={{ height }}>
      <motion.path d={line} fill="none" stroke={color} strokeWidth="2"
        initial={{ pathLength: 0 }} animate={{ pathLength: 1 }} transition={{ duration: 1.5 }}
        style={{ filter: `drop-shadow(0 0 5px ${color})` }} />
      <circle cx={sx(data.length - 1)} cy={sy(data[data.length - 1].y)} r="3" fill="#fff" className="nx-pulse" />
    </svg>
  );
}
